import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { whatsAppLink, WhatsAppIcon } from "@/components/WhatsAppButton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BUSINESS_TYPES = ["Startup", "Freelancer / Consultant", "Private Limited Company", "LLP / Partnership", "E-commerce Seller", "Proprietorship", "Other"];
export const CITY_OPTIONS = ["Kolkata", "Delhi", "Bangalore", "Pune", "Hyderabad", "Mumbai", "Chennai", "Other City"];

export default function InquiryForm({ compact = false, source = "hero", presetMessage = "" }) {
  const [form, setForm] = useState({ name: "", phone: "", business_type: "", city: "", duration: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    if (presetMessage) setForm((f) => ({ ...f, message: presetMessage }));
  }, [presetMessage]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_type || !form.city || !form.duration) {
      toast.error("Please fill all the fields");
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const payload = {
        ...form,
        source,
        utm_source: params.get("utm_source") || "",
        utm_medium: params.get("utm_medium") || "",
        utm_campaign: params.get("utm_campaign") || "",
      };
      await axios.post(`${API}/inquiries`, payload);
      setSubmitted(form);
      toast.success("Inquiry received! Our team will reach out on your number shortly.");
      setForm({ name: "", phone: "", business_type: "", city: "", duration: "", message: "" });
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    const waMsg = `Hi Solve Desk! I just submitted an inquiry.\n\nName: ${submitted.name}\nPhone: ${submitted.phone}\nBusiness: ${submitted.business_type}\nCity: ${submitted.city}\nDuration: ${submitted.duration} months${submitted.message ? `\nNote: ${submitted.message}` : ""}`;
    return (
      <div className="text-center py-6" data-testid="inquiry-success">
        <span className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-5">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
        </span>
        <h3 className="font-display text-2xl font-semibold text-slate-900">Inquiry received!</h3>
        <p className="mt-2 text-sm text-muted-foreground max-w-sm mx-auto">
          Our team will reach out on your number shortly. Want a faster response? Continue on WhatsApp —
          your details are already typed in.
        </p>
        <a href={whatsAppLink(waMsg)} target="_blank" rel="noopener noreferrer" className="block mt-6">
          <Button data-testid="inquiry-whatsapp-continue"
            className="w-full h-14 rounded-full text-base font-semibold bg-[#25D366] hover:bg-[#1fb857] text-white shadow-lg shadow-[#25D366]/25 transition-transform duration-200 hover:-translate-y-0.5">
            <WhatsAppIcon className="mr-2 w-5 h-5" /> Continue on WhatsApp
          </Button>
        </a>
        <button onClick={() => setSubmitted(null)} data-testid="inquiry-submit-another"
          className="mt-4 text-sm text-muted-foreground hover:text-primary transition-colors duration-200 underline underline-offset-4">
          Submit another inquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`inquiry-form-${source}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor={`inq-name-${source}`}>Full Name</Label>
          <Input id={`inq-name-${source}`} data-testid="inquiry-name-input" placeholder="Your name" className="h-12 rounded-xl bg-white"
            value={form.name} onChange={set("name")} required />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`inq-phone-${source}`}>Mobile / WhatsApp No.</Label>
          <Input id={`inq-phone-${source}`} data-testid="inquiry-phone-input" placeholder="+91 98XXX XXXXX" className="h-12 rounded-xl bg-white"
            value={form.phone} onChange={set("phone")} required />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Business Type</Label>
          <Select value={form.business_type} onValueChange={set("business_type")}>
            <SelectTrigger data-testid="inquiry-business-type-select" className="h-12 rounded-xl bg-white">
              <SelectValue placeholder="Select business type" />
            </SelectTrigger>
            <SelectContent>
              {BUSINESS_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>City of Service</Label>
          <Select value={form.city} onValueChange={set("city")}>
            <SelectTrigger data-testid="inquiry-city-select" className="h-12 rounded-xl bg-white">
              <SelectValue placeholder="Select city" />
            </SelectTrigger>
            <SelectContent>
              {CITY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>How long do you need it?</Label>
        <div className="grid grid-cols-3 gap-2" data-testid="inquiry-duration-group">
          {["12", "24", "36"].map((m) => (
            <button type="button" key={m} data-testid={`inquiry-duration-${m}`}
              onClick={() => setForm((f) => ({ ...f, duration: m }))}
              className={`h-12 rounded-xl border text-sm font-medium transition-colors duration-200 ${
                form.duration === m
                  ? "bg-primary text-white border-primary shadow-md"
                  : "bg-white text-slate-700 border-slate-200 hover:border-primary/50"
              }`}>
              {m} months
            </button>
          ))}
        </div>
      </div>
      {!compact && (
        <div className="space-y-1.5">
          <Label htmlFor={`inq-msg-${source}`}>Anything specific? <span className="text-muted-foreground font-normal">(optional)</span></Label>
          <Input id={`inq-msg-${source}`} data-testid="inquiry-message-input" placeholder="e.g. Need GST registration in Bangalore"
            className="h-12 rounded-xl bg-white" value={form.message} onChange={set("message")} />
        </div>
      )}
      <Button type="submit" disabled={loading} data-testid="inquiry-submit-button"
        className="w-full h-14 rounded-full text-base font-semibold bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/25 transition-transform duration-200 hover:-translate-y-0.5">
        {loading ? "Sending..." : "Get Your Virtual Office"}
        {!loading && <ArrowRight className="ml-2 w-5 h-5" />}
      </Button>
      <p className="text-xs text-center text-muted-foreground">
        Our team usually responds within a few hours on WhatsApp.
      </p>
    </form>
  );
}
